import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

import { SERVER_API_URL } from '../../app.constants';
import { AppConfigService } from '../../core/config/app-config.service';
import { CandidateSessionService } from '../../core/auth/candidate-session.service';
import { AccountService } from '../../core/auth/account.service';

/** LB-1893: a full-version feature a Lite user clicked; the menu shows the "Unlock" dialog for it. */
export interface LiteLockedFeature {
  feature: string; // shown name
  moduleKey: string; // entry under menu.lite-locked.modules
  icon: string; // Font Awesome icon name
}

/** LB-1893: details shown in the Lite welcome popup (GET api/account/lite-welcome). */
export interface LiteWelcomeDetails {
  firstName: string;
  lastName: string;
  login: string;
  password: string;
  personalLink: string;
  qrImage: string; // data:image/png;base64,…
}

// Only a flag is stored — never the password.
const PENDING_KEY = 'lbLiteWelcomePending';

// Role of a Lite user+ (Microsoft login on the Lite instance), see LiteRoleSeeder on the server.
const LITE_PLUS_ROLE = 'SYSTEM_LITE_PLUS';
const ADMIN_ROLE = 'SYSTEM_AD';

/** Login (preferred_username) inside a stored access token, or null. */
function loginOf(token: string | null): string | null {
  if (!token) {
    return null;
  }
  try {
    return jwtDecode<{ preferred_username?: string }>(token).preferred_username ?? null;
  } catch {
    return null;
  }
}

/** LB-1893: Leanbyte Lite first-login popup and session check. */
@Injectable({ providedIn: 'root' })
export class LiteWelcomeService {
  private http = inject(HttpClient);
  private appConfig = inject(AppConfigService);
  private candidateSession = inject(CandidateSessionService);
  private accountService = inject(AccountService);
  private watchingOtherTabs = false;

  /** Opens the "Unlock" dialog (shown by the side menu) from any page. */
  readonly locked$ = new Subject<LiteLockedFeature>();

  showLocked(locked: LiteLockedFeature): void {
    this.locked$.next(locked);
  }

  /**
   * Is the signed-in user a Lite user? On the Lite instance every temporary-user session is one. The account's
   * `login` holds "First Last", so a generated Lite user ID is checked on `email`. A Lite user+ (Microsoft login)
   * counts too.
   */
  isLiteSession(email: string | null | undefined): boolean {
    return this.appConfig.leanbyteLiteEnabled
      && ((email ?? '').toLowerCase().endsWith('@lite.leanbyte.io') || this.candidateSession.isActive() || this.isLitePlus());
  }

  /** Lite user+: signed in with Microsoft on the Lite instance and has the Lite user+ role (an admin never is). */
  isLitePlus(): boolean {
    const roles = this.accountService.userIdentity?.roleMemberOf ?? [];
    return this.appConfig.leanbyteLiteEnabled && roles.includes(LITE_PLUS_ROLE) && !roles.includes(ADMIN_ROLE);
  }

  /**
   * All tabs of a browser share one stored login (AuthServerProvider, localStorage "authToken"). When another tab
   * logs in as a different user or logs out, reload this tab so it never shows one user's name with another user's
   * data. A token refresh of the same user does not reload.
   */
  watchLoginInOtherTabs(): void {
    if (!this.appConfig.leanbyteLiteEnabled || this.watchingOtherTabs) {
      return;
    }
    this.watchingOtherTabs = true;
    window.addEventListener('storage', event => {
      // key null = the other tab cleared the whole storage
      if (event.key !== null && event.key !== 'authToken') {
        return;
      }
      const newToken = event.key === null ? null : event.newValue;
      if (loginOf(event.oldValue) !== loginOf(newToken)) {
        window.location.reload();
      }
    });
  }

  markPending(): void {
    try {
      localStorage.setItem(PENDING_KEY, '1');
    } catch {
      /* storage blocked — the popup simply won't show */
    }
  }

  isPending(): boolean {
    try {
      return localStorage.getItem(PENDING_KEY) === '1';
    } catch {
      return false;
    }
  }

  clearPending(): void {
    try {
      localStorage.removeItem(PENDING_KEY);
    } catch {
      /* ignore */
    }
  }

  getDetails(): Observable<LiteWelcomeDetails> {
    return this.http.get<LiteWelcomeDetails>(SERVER_API_URL + 'api/account/lite-welcome', { headers: this.originHeaders() });
  }

  downloadPdf(): Observable<Blob> {
    return this.http.get(SERVER_API_URL + 'api/account/lite-welcome/pdf', { responseType: 'blob', headers: this.originHeaders() });
  }

  // The personal link points at the frontend the user is on (same as the candidate QR sheet).
  private originHeaders(): { [header: string]: string } {
    return { 'X-Frontend-Origin': window.location.origin };
  }
}
